import {isFeatureEnabled} from 'feature-flags';
import {Box, HStack, Icon, Text, VStack} from 'platform/foundation';
import styled, {keyframes} from 'styled-components';

import {useId, useRef, useState} from 'react';

import {suffixTestId, TestIdProps, useOnMount} from 'shared/web';

import i18n from '../../../../../i18n/i18n';
import {featureFlags} from '../../../../../utils/featureFlags';
import {useChartToolbar} from '../hooks/useChartToolbar';
import {getChartEncodingLegendItems} from '../utils/getChartEncodingLegendItems';
import {ChartEncodingLegendIcon} from './ChartEncodingLegendIcon';

interface ChartEncodingLegendProps extends TestIdProps {}

export function ChartEncodingLegend(props: ChartEncodingLegendProps) {
  const {highlights, selectedSellerId} = useChartToolbar();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const legendPanelId = useId();
  const isPinnedRef = useRef(isPinned);
  const isExpandedRef = useRef(isExpanded);

  isPinnedRef.current = isPinned;
  isExpandedRef.current = isExpanded;

  const isSellerTypeEnabled = isFeatureEnabled(featureFlags.PR_FILTERS_SELLER_TYPE);
  const isPriceChangesEnabled = isFeatureEnabled(featureFlags.PR_PRICE_CHANGES);
  const isSuspiciousFlagEnabled = isFeatureEnabled(featureFlags.PR_SUSPICIOUS_FLAG);

  const items = getChartEncodingLegendItems({
    highlights,
    selectedSellerId,
    isSellerTypeEnabled,
    isPriceChangesEnabled,
    isSuspiciousFlagEnabled,
  });

  const collapseLegend = () => {
    setIsPinned(false);
    setIsExpanded(false);
  };

  const collapseLegendRef = useRef(collapseLegend);
  collapseLegendRef.current = collapseLegend;

  useOnMount(function registerLegendDocumentListeners() {
    const handlePointerDown = (event: MouseEvent) => {
      if (!isPinnedRef.current) {
        return;
      }

      if (containerRef.current?.contains(event.target as Node)) {
        return;
      }

      collapseLegendRef.current();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isExpandedRef.current) {
        return;
      }

      if (event.key === 'Escape') {
        collapseLegendRef.current();
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return function removeLegendDocumentListeners() {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  });

  const handleMouseEnter = () => {
    setIsExpanded(true);
  };

  const handleMouseLeave = () => {
    if (!isPinned) {
      setIsExpanded(false);
    }
  };

  const handleToggle = () => {
    if (isExpanded) {
      collapseLegend();
      return;
    }

    setIsPinned(true);
    setIsExpanded(true);
  };

  return (
    <Box ref={containerRef} position="absolute" top={2} right={3} pointerEvents="auto">
      <div
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        data-testid={suffixTestId('wrapper', props)}
      >
        <Box
          id={legendPanelId}
          backgroundColor="palettes.neutral.10.100"
          border="1px solid"
          borderColor="palettes.neutral.30.100"
          borderRadius="small"
          paddingVertical={2}
          paddingHorizontal={2}
          opacity={isExpanded ? 1 : 0.55}
          transition="opacity 0.2s ease, min-width 0.2s ease"
          minWidth={isExpanded ? 40 : undefined}
          width={isExpanded ? undefined : 'auto'}
          data-testid={isExpanded ? suffixTestId('panel', props) : undefined}
        >
          <VStack spacing={isExpanded ? 1 : 0} align="stretch" width="100%">
            <LegendHeaderButton
              type="button"
              aria-expanded={isExpanded}
              aria-controls={legendPanelId}
              onClick={handleToggle}
              data-testid={suffixTestId(isExpanded ? 'collapse' : 'trigger', props)}
            >
              <HStack spacing={1} align="center" justify="flex-end" width="100%">
                <Text size="xSmall" color="secondary" alternative>
                  {i18n.t('entity.overviewEquipment.labels.legend')}
                </Text>
                <RotatingChevron $isExpanded={isExpanded} aria-hidden>
                  <Icon value="navigation/expand_more" size={3} color="text.secondary" />
                </RotatingChevron>
              </HStack>
            </LegendHeaderButton>
            {isExpanded ? (
              <LegendExpandedContent data-testid={suffixTestId('content', props)}>
                <VStack spacing={2}>
                  {items.map((item) => (
                    <HStack key={item.id} spacing={2} align="center">
                      <ChartEncodingLegendIcon item={item} />
                      <Text size="xSmall">{item.label}</Text>
                    </HStack>
                  ))}
                </VStack>
              </LegendExpandedContent>
            ) : null}
          </VStack>
        </Box>
      </div>
    </Box>
  );
}

const legendExpandIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.96);
  }

  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const LegendHeaderButton = styled.button`
  display: block;
  width: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  color: inherit;
  font: inherit;
`;

const RotatingChevron = styled.span<{$isExpanded: boolean}>`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;
  transform: rotate(${({$isExpanded}) => ($isExpanded ? '180deg' : '0deg')});
`;

const LegendExpandedContent = styled.div`
  animation: ${legendExpandIn} 0.2s ease;
`;
